import React, { useState, useContext } from "react";
import axios from "axios";
import Logo from "../../assets/images/arvologo.png";
import checkgif from "../../assets/images/giphy.gif";
import { ProductContext } from "../../context/ProductContext";

// Base64 decryption function
function decryptBase64(base64Str: string): string {
  const neededPadding = base64Str.length % 4;
  if (neededPadding) {
    base64Str += "====".slice(0, 4 - neededPadding);
  }
  return window.atob(base64Str);
}

const Footer: React.FC = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [contactError, setContactError] = useState("");
  const [nameError, setNameError] = useState("");  // Add error for name
  const [reviewError, setReviewError] = useState("");  // Add error for review
  const [review, setReview] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const productContext = useContext(ProductContext);
  if (!productContext) {
    console.error("ProductContext is not provided");
    return null;
  }
  const product = productContext.product;
  if (!product) {
    console.error("Product data is null");
    return null;
  }

  const _colors: Record<number, string> = {
    1: "#f44336",
    2: "#FF5722",
    3: "#FF9800",
    4: "#FFC107",
    5: "#FFEB3B",
  };
  const _meanings: Record<number, string> = {
    0: "No Rating 🚫",
    1: "Terrible 🤮",
    2: "Mediocre 😒",
    3: "Average 😐",
    4: "Solid 🙂",
    5: "Fantastic 🔥",
  };

  const handleRatingClick = (star: number) => {
    setRating(star);
  };

  const handleSave = async () => {
    setContactError("");
    setNameError("");  // Clear name error
    setReviewError("");  // Clear review error

    // 1️⃣ require a rating
    if (rating === 0) {
      alert("Please provide a rating.");
      return;
    }

    // 2️⃣ Name should not be empty
    if (name.trim() === "") {
      setNameError("Please enter your name.");
      return;
    }

    // 3️⃣ Review should not be empty
    if (review.trim() === "") {
      setReviewError("Please write a review.");
      return;
    }

    // 4️⃣ Contact must be valid email or phone
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?\d{7,15}$/;
    if (!emailRegex.test(contact) && !phoneRegex.test(contact)) {
      setContactError(
        "Enter a valid email (foo@bar.com) or phone (+1234567890)."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // ── sentiment analysis ─────────────────────────────────────────
      const sentimentRes = await axios.post(
        "https://4y53b9gp2k.execute-api.ap-south-1.amazonaws.com/prod/sentiment",
        {
          language: "english",
          text: review,
        }
      );
      const sentiment = sentimentRes.data;

      // ── geolocation ───────────────────────────────────────────────
      const geoRes = await axios.get("https://ipapi.co/json/");
      const geoData = {
        ip: geoRes.data.ip,
        city: geoRes.data.city,
        region: geoRes.data.region,
        country_name: geoRes.data.country_name,
        latitude: geoRes.data.latitude,
        longitude: geoRes.data.longitude,
      };

      // ── timestamp & month/year ────────────────────────────────────
      const now = new Date();
      const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;

      // ── extract and decrypt SKU from URL ───────────────────────────
      const params = new URLSearchParams(window.location.search);
      const uid = params.get("uid") || "";
      // Step 1: Decrypt the base64 SKU
      const decryptedSKU = decryptBase64(uid.split("x")[0]);

      // Step 2: Split the SKU at 'x' and take the first part (e.g., 'Phrma001')
      const productSKU = decryptedSKU.split("x")[0];
      console.log("Decrypted SKU:", productSKU); // Log to verify

      // ── assemble review payload ────────────────────────────────────
      const reviewPayload = {
        BrandId: product.brandId,
        ProductSKU: productSKU, // Use the extracted SKU here
        review: {
          ReviewId: `rev-${Date.now()}`,
          Reviewer: name,
          Rating: rating,
          Comment: review,
          Contact: contact,
          Timestamp: new Date().toLocaleString(),
          Sentiment: sentiment,
          Geolocation: geoData,
          Star: rating,
          ProductName: (product as any).title,
          MonthYear: monthYear,
        },
      };

      // ── send review to backend ─────────────────────────────────────
      await fetch(
        "https://qik95uyp2m.execute-api.ap-south-1.amazonaws.com/prod/add-reviews",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reviewPayload),
        }
      );

      setIsSubmitted(true);
    } catch (err) {
      console.error("Error in handleSave:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer
      style={{ backgroundColor: "#201e1e", height: "9rem" }}
      className="w-full py-4 px-6 font-mulish relative"
    >
      <div className="max-w-6xl mx-auto h-full flex flex-col justify-between">
        <div className="flex justify-between items-center">
          <img src={Logo} alt="Company Logo" className="h-10" />
          <p
            className="text-sm text-white cursor-pointer"
            onClick={() => {
              setIsSubmitted(false);
              setRating(0);
              setName("");
              setContact("");
              setReview("");
              setShowPopup(true);
            }}
          >
            Rate this Product
          </p>
        </div>
        <div className="absolute bottom-7 left-6">
          <p className="text-xs text-white">Powered by ARVO © 2025</p>
        </div>
      </div>

      {showPopup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowPopup(false)}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-lg w-80"
            onClick={(e) => e.stopPropagation()}
          >
            {!isSubmitted ? (
              <>
                <h2 className="text-lg font-bold mb-4 text-center">
                  Rate this Product
                </h2>

                {/* Stars */}
                <div className="flex justify-center mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className="cursor-pointer text-2xl"
                      style={{
                        color:
                          star <= rating
                            ? "rgb(255, 215, 75)"
                            : "rgb(156, 163, 175)",
                      }}
                      onClick={() => handleRatingClick(star)}
                    >
                      ★
                    </span>
                  ))}
                </div>

                {/* Meaning */}
                <p
                  className="text-center mb-4 font-semibold"
                  style={{ color: _colors[rating] || "#999" }}
                >
                  {_meanings[rating]}
                </p>

                {/* Name */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full border rounded p-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {nameError && <p className="text-red-500 text-xs mt-1">{nameError}</p>}
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email or phone
                  </label>
                  <input
                    type="text"
                    value={contact}
                    onChange={(e) => {
                      setContact(e.target.value);
                      setContactError("");
                    }}
                    className="w-full border rounded p-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {contactError && (
                    <p className="text-red-500 text-xs mt-1">{contactError}</p>
                  )}
                </div>

                {/* Review */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your review
                  </label>
                  <textarea
                    value={review}
                    onChange={(e) => setReview(e.target.value)}
                    className="w-full border rounded p-2 h-20 resize-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Write a review…"
                  />
                  {reviewError && <p className="text-red-500 text-xs mt-1">{reviewError}</p>}
                </div>

                {/* Buttons */}
                <div className="flex justify-between">
                  <button
                    onClick={() => setShowPopup(false)}
                    className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 transition"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition flex items-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send"}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center">
                <img src={checkgif} alt="success" className="w-16 h-16 mb-2" />
                <p className="text-green-600 font-semibold mb-4">
                  Review sent!
                </p>
                <button
                  onClick={() => setShowPopup(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
